import type { CustomStoreImplementationOptions, KaElementMap, MergeCustomStore, StoreIdentifier } from 'kapix-components-vue3'
import { defineStore } from 'pinia'
import type { WritableComputedRef } from 'vue'
import { NOOP, initAliveStoreIds, kapixContext, remove } from 'kapix-components-vue3'
import pageFixeEnteteComponentStoreCustom from './store.custom'

const storeName = 'pageFixeEnteteComponent'
const customImplement: CustomStoreImplementationOptions = pageFixeEnteteComponentStoreCustom.options
const aliveStoreIds = initAliveStoreIds()

function pageFixeEnteteComponentFactory (storeId?: Nullable<StoreIdentifier>) {
  return defineStore(storeId == null ? storeName : `${storeName}/${storeId}`, {
    state: () => {
      return {
        $aliveStoreIds: aliveStoreIds,
        $subStoreIds: 1,
        $storeId: storeId,
        $emit: (() => {}) as (propName: any, value: any) => void,
        $elements: {} as KaElementMap,
        $router: kapixContext.isClient ? kapixContext.$router : undefined,
        $route: kapixContext.isClient ? kapixContext.$route : undefined,
        $parentPage: NOOP,
        $parentComponent: NOOP,
        // Constants,
        $constants: {},
        // Data,
        $data: {
          h1: undefined as Nullable<string>,
          text1: undefined as Nullable<string>,
          image: undefined as Nullable<string | File>
        },
        ...(customImplement.state && customImplement.state())
      }
    },
    getters: {
      /* @ts-ignore: to allow override in your custom file */
      $h1 (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.h1,
          set: (value) => {
            this.$data.h1 = value
            this.$emit('update:h1', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $text1 (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$data.text1,
          set: (value) => {
            this.$data.text1 = value
            this.$emit('update:text1', value)
          }
        })
      },
      /* @ts-ignore: to allow override in your custom file */
      $image (): WritableComputedRef<Nullable<string | File>> {
        return computed({
          get: () => this.$data.image,
          set: (value) => {
            this.$data.image = value
            this.$emit('update:image', value)
          }
        })
      },
      ...customImplement.getters
    },
    actions: {
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        return storeId != null ? getStoreInstance(storeId) : this
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstances () {
        return aliveStoreIds.map(storeId => this.getStoreInstance(storeId))
      },
      /* @ts-ignore: to allow override in your custom file */
      newStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        const newStoreId = storeId || this.$subStoreIds++
        if (aliveStoreIds.includes(newStoreId)) {
          throw new Error(`Store with id ${storeId} already exists`)
        }
        aliveStoreIds.push(newStoreId)
        return getStoreInstance(newStoreId)
      },
      ...customImplement.actions,
      /* @ts-ignore: to allow override in your custom file */
      beforeMount () {
        customImplement.actions?.beforeMount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      mounted () {
        customImplement.actions?.mounted?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      async serverPrefetch () {
        await customImplement.actions?.serverPrefetch?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      beforeUnmount () {
        customImplement.actions?.beforeUnmount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      unmounted () {
        if (this.$storeId != null) {
          this.$dispose()
          remove(aliveStoreIds, this.$storeId)
        }
        customImplement.actions?.unmounted?.call(this)
      }
    }
  })
}

function getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
  return pageFixeEnteteComponentFactory(storeId)()
}

export const pageFixeEnteteComponentRaw = pageFixeEnteteComponentFactory()
export const pageFixeEnteteComponent = () => pageFixeEnteteComponentRaw() as MergeCustomStore<typeof pageFixeEnteteComponentStoreCustom.instance>
